/**
 * Satzweiser Diff zweier Freitexte (System-/Prüfprompt), für die read-only Diff-Ansicht in
 * `app-change-marker`. Reine Funktion, kein Angular-Bezug – analog zum Stil der übrigen
 * Diff-Module (`mycheck-diff.ts`, `hybrid-mycheck-diff.ts`): kein Bedarf für eine Bibliothek bei
 * dieser Größenordnung.
 *
 * Bewusst NICHT wortgenau: Ein Wort-Diff zerhackt umgeschriebene Sätze in einen Wechsel aus kurzen
 * roten und grünen Schnipseln, der schwer zu lesen ist. Stattdessen wird in Sätze/Zeilen zerlegt;
 * ändert sich innerhalb eines Satzes etwas, erscheint der ganze alte Satz als "removed" und direkt
 * danach der ganze neue Satz als "added".
 */

export interface WordDiffPart {
  type: 'equal' | 'added' | 'removed';
  text: string;
}

/** Ab dieser Zellenzahl (tokens(before) × tokens(after)) wird die klassische O(n·m)-LCS-Berechnung zu
 *  teuer für eine UI-Interaktion – dann lieber ein grober "alles ersetzt"-Diff als eine spürbare Hänger. */
const MAX_DIFF_CELLS = 4_000_000;

/** Häufige deutsche Abkürzungen (ohne Punkt), nach denen kein Satz endet. Einzelbuchstaben-Kürzel
 *  ("z. B.", "d. h.", "u. a.", "i. d. R.") und Ordnungszahlen ("3. Mai", "1. Regel") deckt der
 *  Lookbehind in {@link SENTENCE_PATTERN} separat ab. Steht so eine Abkürzung tatsächlich am Satzende
 *  ("… usw."), wird dort nicht getrennt – das ist der bewusste Kompromiss für lesbarere Diffs. */
const ABBREVIATIONS = 'bzw|ca|evtl|usw|etc|vgl|ggf|inkl|exkl|max|min|nr|abs|tab|abb|kap|std|dr|prof|hr|fr|ff|sog|zzgl|bspw|mind|insb|anm|str|mio|mrd|engl|dt|gem|bez|vs|cf|ebd|resp|sek|art|bd|hrsg|lfd|ehem|geb|zt|zw';

/** Satz = alles bis zu ! ? oder einem Punkt (der nicht hinter Abkürzung/Einzelbuchstabe/kurzer Zahl steht)
 *  vor Whitespace/Textende, oder bis zum Zeilenumbruch; nachfolgender Whitespace gehört noch dazu. */
const SENTENCE_PATTERN = String.raw`.*?(?:[!?]|(?<!(?:^|[^\p{L}\d])(?:\p{L}|\d{1,2}|${ABBREVIATIONS}))\.(?=\s|$)|\n)\s*|.+$`;

/** Zerlegt in Sätze und Zeilen. Der nachfolgende Whitespace bleibt am Satz, damit sich der
 *  Originaltext exakt zusammensetzen lässt. */
function tokenize(text: string): string[] {
  return text.match(new RegExp(SENTENCE_PATTERN, 'gsui'))?.filter(token => token.length > 0) ?? [];
}

export function computeWordDiff(before: string, after: string): WordDiffPart[] {
  const a = tokenize(before ?? '');
  const b = tokenize(after ?? '');
  const n = a.length;
  const m = b.length;

  if (n * m > MAX_DIFF_CELLS) {
    const parts: WordDiffPart[] = [];
    if (before) parts.push({type: 'removed', text: before});
    if (after) parts.push({type: 'added', text: after});
    return parts;
  }

  // Vergleich ohne Whitespace am Ende, damit z. B. ein fehlender Zeilenumbruch am Textende nicht als
  // Änderung des letzten Satzes zählt.
  const keyA = a.map(token => token.trimEnd());
  const keyB = b.map(token => token.trimEnd());

  // dp[i][j] = Länge der längsten gemeinsamen Teilfolge von a[i..] und b[j..].
  const dp: number[][] = Array.from({length: n + 1}, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = keyA[i] === keyB[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const parts: WordDiffPart[] = [];
  const push = (type: WordDiffPart['type'], text: string) => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) {
      last.text += text;
    } else {
      parts.push({type, text});
    }
  };

  // Eine Änderungsgruppe (zusammenhängende entfernte/neue Sätze) wird gesammelt und erst beim nächsten
  // unveränderten Satz ausgegeben: erst alles Alte, dann alles Neue.
  let removed = '';
  let added = '';
  const flush = () => {
    if (removed) {
      // Kein Whitespace am Ende des alten Textes (z. B. letzter Satz) würde alt und neu zusammenkleben.
      push('removed', added && !/\s$/.test(removed) ? removed + ' ' : removed);
    }
    if (added) push('added', added);
    removed = '';
    added = '';
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (keyA[i] === keyB[j]) {
      flush();
      push('equal', b[j]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      removed += a[i];
      i++;
    } else {
      added += b[j];
      j++;
    }
  }
  while (i < n) {
    removed += a[i];
    i++;
  }
  while (j < m) {
    added += b[j];
    j++;
  }
  flush();
  return parts;
}
